/**
 * @fileoverview Web component for the PLP filters/sort modal.
 * Uses Shopify native collection filters (no Algolia).
 */
(() => {
  if (customElements.get('vo-plp-filters')) return;

  const AGE_GROUP_MAP = {
    kids: ['00-03', '04-06', '07-10', '11-13'],
    teen: ['14-17', '11-17'],
  };
  const VO_PLP_FILTER_TEALIUM_EVENT_STORAGE_KEY = 'voPendingPlpFilterTealiumEvent';

  customElements.define('vo-plp-filters', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.form = this.querySelector('[data-plp-filters-form]');
      this.resetBtn = this.querySelector('[data-reset-filters]');
      this.collectionUrl = this.form?.getAttribute('data-collection-url') || this.form?.action || window.location.pathname;

      if (this.dialog) {
        this.dialog.addEventListener('click', this._backdropClick);
        this.dialog.addEventListener('cancel', this._dialogCancel);
      }

      this.form?.addEventListener('submit', this._handleSubmit);
      this.form?.addEventListener('change', this._handleChange);
      this.resetBtn?.addEventListener('click', this._handleReset);
      document.addEventListener('plp:open-filters', this._openFilters);

      this._restoreCheckedState();
    }

    disconnectedCallback() {
      this.dialog?.removeEventListener('click', this._backdropClick);
      this.dialog?.removeEventListener('cancel', this._dialogCancel);
      this.form?.removeEventListener('submit', this._handleSubmit);
      this.form?.removeEventListener('change', this._handleChange);
      this.resetBtn?.removeEventListener('click', this._handleReset);
      document.removeEventListener('plp:open-filters', this._openFilters);
    }

    /** @private */
    _openFilters = () => {
      this.dialog?.showModal();
      document.body.style.overflow = 'hidden';
    }

    /** @private */
    _closeDialog = () => {
      this.dialog?.close();
      document.body.style.overflow = '';
    }

    /** @private */
    _backdropClick = (event) => {
      if (event.target === this.dialog) {
        this._closeDialog();
      }
    }

    /** @private */
    _dialogCancel = (event) => {
      event.preventDefault();
      this._closeDialog();
    }

    /**
     * Updates per-facet active counts when checkboxes change
     * and fetches the real product count from the server.
     * @private
     */
    _handleChange = () => {
      this.form?.querySelectorAll('[data-facet]').forEach(facet => {
        const checked = facet.querySelectorAll('input[type="checkbox"]:checked').length;
        const countEl = facet.querySelector('[data-facet-count]');
        if (countEl) {
          countEl.textContent = checked > 0 ? `(${checked})` : '';
        }
      });

      this._fetchProductCount();
    }

    /**
     * Fetches the filtered product count and updates the result display.
     * In Storefront API engine mode, counts grouped products via the
     * Storefront API. Otherwise falls back to the server-rendered HTML.
     * Debounced to avoid excessive requests.
     * @private
     */
    _fetchProductCount = (() => {
      let controller;
      let timer;

      return () => {
        clearTimeout(timer);
        controller?.abort();

        timer = setTimeout(async () => {
          const resultCountEl = this.querySelector('[data-result-count]');
          if (!resultCountEl) return;

          controller = new AbortController();
          const formData = new FormData(this.form);
          const params = new URLSearchParams();

          for (const [key, value] of formData.entries()) {
            if (value !== '' && value != null) {
              params.append(key, value);
            }
          }

          this._expandAgeGroups(params);

          if (window.voPlpConfig) {
            try {
              const total = await this._countGroupedViaStorefront(params, controller.signal);
              if (total != null) resultCountEl.textContent = total;
            } catch (e) {
              if (e.name !== 'AbortError') console.error('[vo-plp-filters] storefront count error', e);
            }
            return;
          }

          const sectionId = this.form?.id?.replace('filterForm-', '');
          if (sectionId) params.append('section_id', sectionId);

          try {
            const url = `${this.collectionUrl}?${params.toString()}`;
            const res = await fetch(url, {
              signal: controller.signal,
              headers: { Accept: 'text/html' },
            });

            if (!res.ok) return;

            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');

            let total = null;

            const countEl = doc.getElementById('ProductCount');
            if (countEl) {
              total = countEl.getAttribute('data-total');
            }

            if (total == null) {
              const titleCount = doc.querySelector('.vo-collection-plp__title-count');
              if (titleCount) {
                const match = titleCount.textContent.match(/\d+/);
                if (match) total = match[0];
              }
            }

            if (total == null) {
              const emptyState = doc.querySelector('.collection--empty');
              if (emptyState) total = '0';
            }

            if (total != null) {
              resultCountEl.textContent = total;
            }
          } catch (e) {
            if (e.name !== 'AbortError') console.error('[vo-plp-filters] count fetch error', e);
          }
        }, 300);
      };
    })()

    /**
     * Counts grouped products via the Storefront API for filter preview.
     * Converts form URLSearchParams to Storefront API filter objects,
     * paginates through the collection, and deduplicates by model code.
     * @param {URLSearchParams} params - filter params from the form
     * @param {AbortSignal} signal
     * @returns {Promise<number>}
     * @private
     */
    async _countGroupedViaStorefront(params, signal) {
      const cfg = window.voPlpConfig;
      const handle = cfg.collection?.handle;
      if (!handle) return null;

      const sf = cfg.storefront || window.storefront || {};
      const mkt = cfg.market || {};
      const perFetch = 200;
      const sortBy = params.get('sort_by') || 'manual';
      params.delete('sort_by');
      const sortMap = {
        'manual':             { sortKey: 'COLLECTION_DEFAULT', reverse: false },
        'best-selling':       { sortKey: 'BEST_SELLING',       reverse: false },
        'title-ascending':    { sortKey: 'TITLE',              reverse: false },
        'title-descending':   { sortKey: 'TITLE',              reverse: true  },
        'price-ascending':    { sortKey: 'PRICE',              reverse: false },
        'price-descending':   { sortKey: 'PRICE',              reverse: true  },
        'created-ascending':  { sortKey: 'CREATED',            reverse: false },
        'created-descending': { sortKey: 'CREATED',            reverse: true  },
      };
      const { sortKey, reverse } = sortMap[sortBy] || sortMap['manual'];

      const filters = [];
      const price = {};
      for (const [k, v] of params.entries()) {
        if (!k.startsWith('filter.')) continue;
        if (k === 'filter.v.availability') { filters.push({ available: v === '1' || v === 'true' }); continue; }
        if (k === 'filter.p.tag') { filters.push({ tag: v }); continue; }
        const pmf = k.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
        if (pmf) { filters.push({ productMetafield: { namespace: pmf[1], key: pmf[2], value: v } }); continue; }
        const vmf = k.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
        if (vmf) { filters.push({ variantMetafield: { namespace: vmf[1], key: vmf[2], value: v } }); continue; }
        if (k === 'filter.v.price.gte') { price.min = parseFloat(v); continue; }
        if (k === 'filter.v.price.lte') { price.max = parseFloat(v); continue; }
        const opt = k.match(/^filter\.v\.option\.(.+)$/);
        if (opt) { filters.push({ variantOption: { name: opt[1], value: v } }); continue; }
      }
      if (Object.keys(price).length) filters.push({ price });

      const query = `query($handle:String!,$first:Int!,$after:String,$filters:[ProductFilter!],$sortKey:ProductCollectionSortKeys,$reverse:Boolean,$country:CountryCode!,$language:LanguageCode!)@inContext(country:$country,language:$language){collection(handle:$handle){products(first:$first,after:$after,filters:$filters,sortKey:$sortKey,reverse:$reverse){edges{node{handle,modelCode:metafield(namespace:"custom",key:"model_code_display"){value}}}pageInfo{hasNextPage,endCursor}}}}`;

      let cursor = null;
      let hasNext = true;
      const seen = new Set();

      while (hasNext) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

        const res = await fetch(sf.url, {
          method: 'POST',
          signal,
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': sf.token,
          },
          body: JSON.stringify({
            query,
            variables: {
              handle, first: perFetch, after: cursor || null,
              filters: filters.length ? filters : [],
              sortKey, reverse,
              language: mkt.language || 'EN',
              country: mkt.country || 'US',
            },
          }),
        });

        if (!res.ok) return null;
        const json = await res.json();
        const col = json.data?.collection;
        if (!col) return 0;

        const products = col.products.edges.map((e) => e.node);
        for (const p of products) {
          const key = p.modelCode?.value || p.handle;
          seen.add(key);
        }

        hasNext = col.products.pageInfo.hasNextPage;
        cursor = col.products.pageInfo.endCursor;
        if (!products.length) break;
      }

      return seen.size;
    }

    /**
     * Expand _vo_age_group checkboxes into real filter params.
     * @param {URLSearchParams} params
     * @private
     */
    _expandAgeGroups(params) {
      const groups = params.getAll('_vo_age_group');
      params.delete('_vo_age_group');
      for (const group of groups) {
        const ranges = AGE_GROUP_MAP[group];
        if (!ranges) continue;
        for (const range of ranges) {
          params.append('filter.p.m.custom.child_age_range', range);
        }
      }
    }

    /**
     * Formats Shopify filter query params using the Persol PLP Tealium contract.
     * @param {string} key
     * @param {string} value
     * @returns {string}
     * @private
     */
    _formatFacetParamForTealium(key, value) {
      const rawKey = String(key || '').trim();
      const rawValue = String(value || '').trim();
      if (!rawKey || !rawValue) return '';

      let facetName = rawKey
        .replace(/^filter\.[vp]\.m\.custom\./, '')
        .replace(/^filter\.p\.m\./, '')
        .replace(/^filter\.v\.m\./, '')
        .replace(/^filter\.v\.option\./, '')
        .replace(/^filter\.[vp]\./, '')
        .replace(/^m\.custom\./, '')
        .replace(/^custom\./, '');

      if (facetName === 'sort_by') facetName = 'sortby';
      return `${facetName}=${rawValue}`.replace(/\s+/g, '');
    }

    /**
     * @param {URLSearchParams} params
     * @returns {string}
     * @private
     */
    _buildFacetValuesString(params) {
      const facets = [];

      params.forEach((value, key) => {
        if (key === 'page' || key === 'section_id') return;
        if (!key.startsWith('filter.') && key !== 'sort_by') return;

        const facet = this._formatFacetParamForTealium(key, value);
        if (facet) facets.push(facet);
      });

      return facets.join('|');
    }

    /**
     * @param {URLSearchParams} params
     * @param {string} targetUrl
     * @returns {object}
     * @private
     */
    _buildFilterTrackingData(params, targetUrl) {
      const resultCount = this.querySelector('[data-result-count]')?.textContent?.match(/\d+/)?.[0] || '';

      return {
        id: 'Event',
        Event_Source: 'PLP',
        Events_SearchFiltering: '1',
        Search_ResultItemsQnt: resultCount,
        Search_FacetValues_String: this._buildFacetValuesString(params),
        targetUrl,
      };
    }

    /**
     * Persists the PLP filter payload so Tealium can emit it after navigation.
     * @param {URLSearchParams} params
     * @param {string} targetUrl
     * @private
     */
    _storeFilterTrackingData(params, targetUrl) {
      try {
        const trackingData = this._buildFilterTrackingData(params, targetUrl);
        const hasActiveFacet = Boolean(trackingData.Search_FacetValues_String);
        const currentParams = new URLSearchParams(window.location.search);
        const hadActiveFacet = Boolean(this._buildFacetValuesString(currentParams));

        if (!hasActiveFacet && !hadActiveFacet) {
          sessionStorage.removeItem(VO_PLP_FILTER_TEALIUM_EVENT_STORAGE_KEY);
          return;
        }

        sessionStorage.setItem(VO_PLP_FILTER_TEALIUM_EVENT_STORAGE_KEY, JSON.stringify(trackingData));
      } catch (error) {
        console.warn('[vo-plp-filters] Tealium filter tracking storage failed', error);
      }
    }

    /**
     * Builds the filter URL from the form data and navigates.
     * @private
     */
    _handleSubmit = (event) => {
      event.preventDefault();
      const formData = new FormData(this.form);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value !== '' && value != null) {
          params.append(key, value);
        }
      }

      this._expandAgeGroups(params);

      const queryString = params.toString();
      const targetUrl = queryString
          ? `${this.collectionUrl}?${queryString}`
          : this.collectionUrl;

      this._storeFilterTrackingData(params, targetUrl);
      window.location.href = targetUrl;
    }

    /**
     * Restores checkbox/radio checked state from URL query parameters.
     * Covers hardcoded pill filters that Liquid may not mark as checked,
     * and maps age-range params back to the Kids/Teen virtual pills.
     * @private
     */
    _restoreCheckedState() {
      if (!this.form) return;
      const params = new URLSearchParams(window.location.search);
      const ageValues = params.getAll('filter.p.m.custom.child_age_range');

      for (const [group, ranges] of Object.entries(AGE_GROUP_MAP)) {
        if (ranges.some((r) => ageValues.includes(r))) {
          const pill = this.form.querySelector(
            `input[name="_vo_age_group"][value="${CSS.escape(group)}"]`
          );
          if (pill && !pill.checked) pill.checked = true;
        }
      }

      for (const [key, value] of params.entries()) {
        if (key === 'filter.p.m.custom.child_age_range') continue;
        const input = this.form.querySelector(
          `input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
        );
        if (input && !input.checked) input.checked = true;
      }
    }

    /**
     * Navigates to the clean collection URL (no filters/sort).
     * @private
     */
    _handleReset = () => {
      this.form?.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
      this.form?.querySelectorAll('input[type="radio"]').forEach(rb => { rb.checked = false; });
      this.form?.querySelectorAll('input[type="number"]').forEach(nb => { nb.value = ''; });

      this._storeFilterTrackingData(new URLSearchParams(), this.collectionUrl);
      window.location.href = this.collectionUrl;
    }
  });
})();
